import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { employeesApi } from '@/services/api/endpoints/staff';
import { queryKeys } from '@/services/api/queryKeys';
import type { UUID } from '@/types/domain';
import { orDash } from '@/utils/format';

export interface PickedEmployee {
  id: UUID;
  name: string;
  number?: string;
}

/**
 * Finding the member of staff a leave request, salary or loan belongs to.
 *
 * <p>Search rather than a dropdown: a school has hundreds of employees and the
 * person filling this in has a name and possibly an employee number. The number
 * is shown beside every result because two people called Ahmed Ali is not a
 * hypothetical, and picking the wrong one puts a loan against the wrong salary.
 */
export function EmployeePicker({
  value,
  onChange,
  label = 'Employee',
  required = true,
}: {
  value: PickedEmployee | null;
  onChange: (employee: PickedEmployee | null) => void;
  label?: string;
  required?: boolean;
}) {
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term, 300);

  const results = useQuery({
    queryKey: queryKeys.employees.list({ picker: true, search }),
    queryFn: () => employeesApi.list({ page: 0, size: 8, search: search.trim() }),
    // Two characters before asking: one letter matches most of the school and
    // costs a request per keystroke to say so.
    enabled: value === null && search.trim().length >= 2,
  });

  if (value !== null) {
    return (
      <div>
        <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>
        <div className="flex items-center justify-between gap-3 border border-line bg-surface-2 px-3 py-2">
          <span>
            <span className="block text-sm font-medium text-ink">{value.name}</span>
            <span className="block font-mono text-[11px] text-subtle">{orDash(value.number)}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setTerm('');
            }}
            className="flex h-7 w-7 items-center justify-center border border-line text-muted transition-colors hover:bg-surface hover:text-ink"
            aria-label="Choose a different employee"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="employee-search" className="mb-1.5 block text-xs font-medium text-muted">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
          aria-hidden="true"
        />
        <input
          id="employee-search"
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search by name, number or e-mail…"
          autoComplete="off"
          className="block w-full border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
        />
      </div>

      {search.trim().length >= 2 && (
        <div className="mt-1 max-h-56 overflow-y-auto border border-line bg-surface">
          {results.isPending ? (
            <p className="px-3 py-3 text-[13px] text-muted">Searching…</p>
          ) : (results.data?.data ?? []).length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-muted">
              Nobody matches “{search.trim()}”.
            </p>
          ) : (
            (results.data?.data ?? []).map((employee) => (
              <button
                key={employee.id}
                type="button"
                onClick={() =>
                  onChange({
                    id: employee.id,
                    name: employee.fullName ?? employee.employeeNumber,
                    number: employee.employeeNumber,
                  })
                }
                className="flex w-full items-center justify-between gap-3 border-b border-line px-3 py-2 text-left last:border-0 hover:bg-surface-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink">
                    {employee.fullName ?? employee.employeeNumber}
                  </span>
                  {employee.designation !== undefined && (
                    <span className="block truncate text-[11px] text-subtle">
                      {employee.designation}
                    </span>
                  )}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-subtle">
                  {employee.employeeNumber}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
